import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import MarketingLayout from './layouts/MarketingLayout';
import DashboardLayout from './layouts/DashboardLayout';
import AdminLayout from './layouts/AdminLayout';
import InstructorLayout from './layouts/InstructorLayout';
import ModeratorLayout from './layouts/ModeratorLayout';
import LearningHubPage from './pages/LearningHubPage';
import WorkplaceLogPage from './pages/WorkplaceLogPage';
import LibraryPage from './pages/LibraryPage';
import AdminModeratorsPage from './pages/admin/AdminModeratorsPage';
import AdminCompliancePage from './pages/admin/AdminCompliancePage';
import AdminStudentDocumentsPage from './pages/admin/AdminStudentDocumentsPage';
import AdminApplicationsPage from './pages/admin/AdminApplicationsPage';
import ModeratorDashboardPage from './pages/moderator/ModeratorDashboardPage';
import ModeratorCoursePage from './pages/moderator/ModeratorCoursePage';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import LearnershipsPage from './pages/LearnershipsPage';
import ShortProgrammesPage from './pages/ShortProgrammesPage';
import ApplyPage from './pages/ApplyPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StudentProfilePage from './pages/StudentProfilePage';
import MyLearnershipsPage from './pages/MyLearnershipsPage';
import ProgressPage from './pages/ProgressPage';
import MessagesPage from './pages/MessagesPage';
import DownloadsPage from './pages/DownloadsPage';
import AssignmentsPage from './pages/AssignmentsPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminCoursesPage from './pages/admin/AdminCoursesPage';
import AdminStudentsPage from './pages/admin/AdminStudentsPage';
import AdminInstructorsPage from './pages/admin/AdminInstructorsPage';
import AdminCreateUserPage from './pages/admin/AdminCreateUserPage';
import InstructorDashboardPage from './pages/instructor/InstructorDashboardPage';
import InstructorProfilePage from './pages/instructor/InstructorProfilePage';
import InstructorCoursesPage from './pages/instructor/InstructorCoursesPage';
import InstructorCourseDetailPage from './pages/instructor/InstructorCourseDetailPage';
import InstructorStudentsPage from './pages/instructor/InstructorStudentsPage';
import InstructorStudentDetailPage from './pages/instructor/InstructorStudentDetailPage';
import InstructorAssignmentGradingPage from './pages/instructor/InstructorAssignmentGradingPage';
import InstructorGradebookPage from './pages/instructor/InstructorGradebookPage';
import Placeholder from './pages/Placeholder';
import NotFound from './pages/NotFound';
import ScrollToTop from './components/layout/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          {/* Marketing pages */}
          <Route element={<MarketingLayout />}>
            <Route index element={<Home />} />
            <Route path="learnerships" element={<LearnershipsPage />} />
            <Route path="learnerships/:saqaId" element={<LearnershipsPage />} />
            <Route path="short-programmes" element={<ShortProgrammesPage />} />
            <Route path="apply" element={<ApplyPage />} />
            <Route path="about" element={<About />} />
            <Route path="contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Authentication pages */}
          <Route path="sign-in" element={<LoginPage />} />
          <Route
            path="register"
            element={
              <Placeholder
                title="Create account"
                description="Account creation arrives with the API authentication layer. For now, apply by email."
              />
            }
          />

          {/* Protected dashboard routes */}
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="profile" element={<StudentProfilePage />} />
            <Route path="learnerships" element={<MyLearnershipsPage />} />
            <Route path="progress" element={<ProgressPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="downloads" element={<DownloadsPage />} />
            <Route path="assignments" element={<AssignmentsPage />} />
            <Route path="hub" element={<LearningHubPage />} />
            <Route path="workplace" element={<WorkplaceLogPage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route
              path="*"
              element={
                <Placeholder
                  title="Coming Soon"
                  description="This dashboard section is being built."
                />
              }
            />
          </Route>

          {/* Protected lecturer routes */}
          <Route
            path="instructor"
            element={
              <ProtectedRoute requiredRole={1}>
                <InstructorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<InstructorDashboardPage />} />
            <Route path="dashboard" element={<InstructorDashboardPage />} />
            <Route path="profile" element={<InstructorProfilePage />} />
            <Route path="courses" element={<InstructorCoursesPage />} />
            <Route path="courses/:learnershipId" element={<InstructorCourseDetailPage />} />
            <Route path="students" element={<InstructorStudentsPage />} />
            <Route path="students/:studentId" element={<InstructorStudentDetailPage />} />
            <Route path="assignments/:assignmentId" element={<InstructorAssignmentGradingPage />} />
            <Route path="gradebook" element={<InstructorGradebookPage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route
              path="*"
              element={
                <Placeholder
                  title="Coming Soon"
                  description="This lecturer section is being built."
                />
              }
            />
          </Route>

          {/* Protected moderator routes (read-only, assigned courses) */}
          <Route
            path="moderator"
            element={
              <ProtectedRoute requiredRole={3}>
                <ModeratorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ModeratorDashboardPage />} />
            <Route path="dashboard" element={<ModeratorDashboardPage />} />
            <Route path="courses" element={<ModeratorDashboardPage title="My Courses" />} />
            <Route path="courses/:learnershipId" element={<ModeratorCoursePage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route
              path="*"
              element={
                <Placeholder
                  title="Coming Soon"
                  description="This moderator section is being built."
                />
              }
            />
          </Route>

          {/* Protected admin routes */}
          <Route
            path="admin"
            element={
              <ProtectedRoute requiredRole={2}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="courses" element={<AdminCoursesPage />} />
            <Route path="students" element={<AdminStudentsPage />} />
            <Route path="instructors" element={<AdminInstructorsPage />} />
            <Route path="moderators" element={<AdminModeratorsPage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="compliance" element={<AdminCompliancePage />} />
            <Route path="student-documents" element={<AdminStudentDocumentsPage />} />
            <Route path="applications" element={<AdminApplicationsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="users/new" element={<AdminCreateUserPage />} />
            <Route
              path="*"
              element={
                <Placeholder
                  title="Coming Soon"
                  description="This admin section is being built."
                />
              }
            />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
